import React, {useState, useEffect} from 'react'
import {Form} from 'antd'
import {QuestionCircleOutlined} from '@ant-design/icons'

import {OnboardingAlert} from '@/pages/onboarding/components/OnboardingAlert'
import {useScreenType} from '@/hooks'

import {FormItemProps} from './props'
import styles from './FormItem.scss'

export const FormItem: React.FC<FormItemProps> = ({
    name,
    label,
    tooltip,
    onboardingAlert,
    validation = [],
    rules = [],
    required = false,
    children,
    extra,
    className,
    valuePropName,
    validateStatus
}) => {
    const {isMobile, isTablet} = useScreenType()
    const isMobileView = isMobile || isTablet

    const finalRules = rules.length > 0 ? rules : [...validation]

    if (required && finalRules.length === 0) {
        finalRules.unshift({required: true, message: 'This field is required'})
    }

    const formItemProps: any = {
        name,
        className,
        rules: finalRules,
        extra,
        validateStatus
    }

    if (valuePropName) {
        formItemProps.valuePropName = valuePropName
    }

    const [isAlertVisible, setIsAlertVisible] = useState(false)
    const [alertTimeoutId, setAlertTimeoutId] = useState<NodeJS.Timeout | null>(null)

    useEffect(() => {
        return () => {
            if (alertTimeoutId) {
                clearTimeout(alertTimeoutId)
            }
        }
    }, [alertTimeoutId])

    const handleIconClick = () => {
        if (isMobileView || onboardingAlert?.trigger === 'click') {
            setIsAlertVisible(!isAlertVisible)
            if (alertTimeoutId) {
                clearTimeout(alertTimeoutId)
            }
        }
    }

    const handleIconHover = () => {
        if (!isMobileView && (onboardingAlert?.trigger === 'hover' || !onboardingAlert?.trigger)) {
            if (alertTimeoutId) {
                clearTimeout(alertTimeoutId)
            }
            setIsAlertVisible(true)
        }
    }

    const handleIconLeave = () => {
        if (!isMobileView && (onboardingAlert?.trigger === 'hover' || !onboardingAlert?.trigger)) {
            const timeoutId = setTimeout(() => {
                setIsAlertVisible(false)
            }, 200)
            setAlertTimeoutId(timeoutId)
        }
    }

    const handleBackdropClick = () => {
        if (isMobileView && isAlertVisible) {
            setIsAlertVisible(false)
        }
    }

    return (
        <div className={onboardingAlert ? styles.formItemContainer : undefined}>
            <Form.Item
                className={styles.formItem}
                {...formItemProps}
                label={
                    onboardingAlert ? (
                        <div className={styles.labelContainer}>
                            <span>{label}</span>
                            <QuestionCircleOutlined
                                className={styles.alertTriggerIcon}
                                onClick={handleIconClick}
                                onMouseEnter={handleIconHover}
                                onMouseLeave={handleIconLeave}
                            />
                        </div>
                    ) : (
                        label
                    )
                }
                tooltip={!onboardingAlert ? tooltip : undefined}
            >
                {children}
            </Form.Item>

            {onboardingAlert && (
                <OnboardingAlert
                    visible={isAlertVisible}
                    text={onboardingAlert.text}
                    title={onboardingAlert.title}
                    content={onboardingAlert.content}
                    variant={onboardingAlert.variant || 'info'}
                    onBackdropClick={handleBackdropClick}
                />
            )}
        </div>
    )
}
